/**
 * Tools to perform a deep merge of two objects. This is a pretty unwieldy and dynamic tool, so make sure
 * you know what you're doing here.
 */

/* eslint-disable */

export function isMergeableObject(val: any): boolean {
  var nonNullObject = val && typeof val === "object";

  return (
    nonNullObject &&
    Object.prototype.toString.call(val) !== "[object RegExp]" &&
    Object.prototype.toString.call(val) !== "[object Date]"
  );
}

function emptyTarget(val: any): any[] | Record<string, any> {
  return Array.isArray(val) ? [] : {};
}

export interface MergeOptions {
  readonly clone?: boolean;
  readonly arrayMerge?: typeof defaultArrayMerge;
  readonly omit?: { [key: string]: boolean };
}

function cloneIfNecessary(value: any, optionsArgument?: MergeOptions): any {
  var clone = optionsArgument && optionsArgument.clone === true;
  return clone && isMergeableObject(value) ? deepmerge(emptyTarget(value), value, optionsArgument) : value;
}

function defaultArrayMerge(target: any[], source: any[], optionsArgument?: MergeOptions): any[] {
  var destination = target.slice();
  source.forEach(function (e: any, i: number) {
    if (typeof destination[i] === "undefined") {
      destination[i] = cloneIfNecessary(e, optionsArgument);
    } else if (isMergeableObject(e)) {
      destination[i] = deepmerge(target[i], e, optionsArgument);
    } else if (target.indexOf(e) === -1) {
      destination.push(cloneIfNecessary(e, optionsArgument));
    }
  });
  return destination;
}

function mergeObject(
  target: Record<string, any>,
  source: Record<string, any>,
  optionsArgument?: MergeOptions,
): Record<string, any> {
  var destination: Record<string, any> = {};
  if (isMergeableObject(target)) {
    Object.keys(target).forEach(function (key: string) {
      destination[key] = cloneIfNecessary(target[key], optionsArgument);
    });
  }
  Object.keys(source).forEach(function (key: string) {
    if (optionsArgument?.omit?.[key]) {
      return;
    }
    if (!isMergeableObject(source[key]) || !target[key]) {
      destination[key] = cloneIfNecessary(source[key], optionsArgument);
    } else {
      destination[key] = deepmerge(target[key], source[key], optionsArgument);
    }
  });
  return destination;
}

export default function deepmerge(target: any, source: any, optionsArgument?: MergeOptions): any {
  var array = Array.isArray(source);
  var options = optionsArgument || { arrayMerge: defaultArrayMerge };
  var arrayMerge = options.arrayMerge || defaultArrayMerge;

  if (array) {
    return Array.isArray(target)
      ? arrayMerge(target, source, optionsArgument)
      : cloneIfNecessary(source, optionsArgument);
  } else {
    return mergeObject(target, source, optionsArgument);
  }
}
